import type { FC } from 'react';
import { Flex, Icon, Section, Skeleton, Typography } from '@ui';
import { useTranslation } from 'react-i18next';
import { useAirportMetar } from '@/queries/useAirportMetar';
import { useAirportTaf } from '@/queries/useAirportTaf';

type Props = {
    airportId?: number;
    section?: boolean;
};

type Subcomponents = {
    Skeleton: FC<Pick<Props, 'section'>>;
};

const Weather: FC<Props> & Subcomponents = ({ airportId = 1, section }) => {
    const { t } = useTranslation();
    const { data: { metar } } = useAirportMetar(airportId);
    const { data: { taf } } = useAirportTaf(airportId);
    const weather: string[] = [metar, taf];

    const innerContent = metar && taf
        ? (
                <Flex vertical gap={section ? 24 : 16} style={section ? { width: 576 } : undefined}>
                    {weather.map(item => (
                        <Section key={item} variant="filled">
                            <Typography.Paragraph level={2}>
                                {item}
                            </Typography.Paragraph>
                        </Section>
                    ))}
                </Flex>
            )
        : (
                <Flex vertical align="center" gap={8}>
                    <Icon.Flood size={48} color="textTertiary" />
                    <Typography.Paragraph level={2} color="textSecondary">{t('Airports_Card_Weather_Empty')}</Typography.Paragraph>
                </Flex>
            );

    if (section) {
        return (
            <Section size="l">
                {innerContent}
            </Section>
        );
    }

    return innerContent;
};

const weatherWidths = Object.entries({ metar: ['90%', '10%'], taf: ['88%', '92%', '10%'] });

Weather.Skeleton = ({ section }) => {
    const innerContent = (
        <Flex vertical gap={section ? 24 : 16} style={section ? { width: 576 } : undefined}>
            {weatherWidths.map(([line, widths]) => (
                <Section key={line} variant="filled" block>
                    <Flex vertical gap={4}>
                        {widths.map(width => (
                            <Skeleton key={width} width={width} height={16} />
                        ))}
                    </Flex>
                </Section>
            ))}
        </Flex>
    );

    if (section) {
        return (
            <Section size="l">
                {innerContent}
            </Section>
        );
    }

    return innerContent;
};

export { Weather };
